import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  FiltersWrapper,
  FiltersList,
  FilterHeadWrapper,
  TitleAndClose,
  Title,
  HideFilters,
} from './Filters.style';
import OfferTypeSelector from './OfferType/OfferType';
import useFilterOptions from '../../hooks/useFilterOptions';
import { useAvailableFilterOptions } from '../../hooks/useAvailableFilterOptions';
import { ALL_FILTERS_OPENED, INTIAL_FILTERS } from '../../constants/initialFilters';
import {
  addOrRemoveTempFilter,
  selectFilters,
  clearAllFilters,
} from '../../reducers/filtersReducer';
import BodyStyle from './BodyStyle/BodyStyleList';
import TrimLineSelector from './TrimLine/TrimLineSelector';
import ModelYearSelector from './ModelYear/ModelYearSelector';
import ModelFamilySelector from './ModelFamily/ModelFamilySelector';
import CtaGroup from './CtaGroup';
import { MultipleSelectChangeEventComponent } from '../../types/analytics';
import {
  filterChange,
  filterClearAll,
  filterCurrentState,
} from '../../utils/analyticsEventListeners';
import CloseIcon from '../../assets/icons/CloseIcon';
import BackIcon from '../../assets/icons/BackIcon';
import ZipInput from './ZipInput';
import useScrollTracker from '../../hooks/useScrollTracker';
import getFinalFiltersList from '../../utils/getFinalFiltersList';
import { selectOffers } from '../../reducers/offersReducer';

type FiltersProps = {
  isLaptop: boolean;
  isReachedEndOfContainer: boolean;
  setIsFiltersOpen: (arg: boolean) => void;
};

const Filters: React.FC<FiltersProps> = ({
  isLaptop,
  isReachedEndOfContainer,
  setIsFiltersOpen,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [openedFiltersList, setOpenedFiltersList] = useState(ALL_FILTERS_OPENED);

  const { confirmedFilters } = useSelector(selectFilters);
  const { offersList } = useSelector(selectOffers);

  const listRef = useRef(null);

  const {
    offerTypeOptions,
    bodyTypeOptions,
    modelFamilyOptions,
    trimLineOptions,
    modelYearOptions,
  } = useFilterOptions();
  const {
    availableOfferTypeOptions,
    availableBodyTypeOptions,
    availableModelFamilyOptions,
    availableTrimLineOptions,
    availableModelYearOptions,
    totalAvailableOffers,
  } = useAvailableFilterOptions();

  const handleAddFilter = (filterType: string, filterValue: string, isChecked?: boolean) => {
    dispatch(addOrRemoveTempFilter({ filterType, filterValue }));
    const payloadForIxis: MultipleSelectChangeEventComponent = {
      action: !isChecked ? 'select' : 'deselect',
      filterName: filterType,
      value: filterValue,
    };

    const { latestFilterState, totalCount } = getFinalFiltersList(
      filterType,
      filterValue,
      confirmedFilters,
      offersList,
    );

    filterChange({ payloadForIxis, latestFilterState, totalCount });
  };

  const handleExposeFilter = (filterType: string) => {
    setOpenedFiltersList((prevState) => ({ ...prevState, [filterType]: !prevState[filterType] }));
  };

  const handleCloseFilters = () => {
    setIsFiltersOpen(false);
  };

  const isPassedNav = useScrollTracker(72);

  const handleClearAll = () => {
    const region = offersList[0]?.zipClusterID;
    filterClearAll(confirmedFilters, region);
    dispatch(clearAllFilters());
    filterCurrentState(INTIAL_FILTERS);
  };

  return (
    <FiltersWrapper ref={listRef}>
      <FilterHeadWrapper>
        <TitleAndClose>
          <Title>{t('promo.filters.title')}</Title>
          <HideFilters
            onClick={() => {
              setIsFiltersOpen(false);
            }}
          >
            {isLaptop ? (
              <>
                <BackIcon />
                {t('promo.filters.hideFilters')}
              </>
            ) : (
              <CloseIcon />
            )}
          </HideFilters>
        </TitleAndClose>
      </FilterHeadWrapper>
      <FiltersList isReachedEndOfContainer={isReachedEndOfContainer} isPassedNav={isPassedNav}>
        <ZipInput />
        <OfferTypeSelector
          filterType="offerType"
          offerTypeOptions={offerTypeOptions}
          openedFiltersList={openedFiltersList}
          availableFilters={availableOfferTypeOptions}
          confirmedFilters={confirmedFilters}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
        <BodyStyle
          filterType="bodyType"
          bodyTypeOptions={bodyTypeOptions}
          availableFilters={availableBodyTypeOptions}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
        <ModelFamilySelector
          filterType="modelFamily"
          modelFamilyOptions={modelFamilyOptions}
          availableFilters={availableModelFamilyOptions}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
        <ModelYearSelector
          filterType="modelYear"
          modelYearOptions={modelYearOptions}
          availableFilters={availableModelYearOptions}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      </FiltersList>
      {!isLaptop && (
        <CtaGroup
          totalCount={totalAvailableOffers}
          closeFilter={handleCloseFilters}
          onReset={handleClearAll}
        />
      )}
    </FiltersWrapper>
  );
};

export default Filters;
